import { defineStore } from "pinia"
import { login, logout, getInfo, phoneLogin } from '@/api/login'
import { getToken, setToken, removeToken } from '@/utils/auth'
import defAva from '@/assets/images/profile.png'
import defLogo from '@/assets/logo/logo.png'
import defVisual from '@/assets/logo/visual.png'
import { ws_req } from '@/ws/wss-request'
import { useAppStore } from '@/store/modules/app'
// 导入 chat-init-manager 和 setRequestConfig 用于清理 chatId 和 sessionId
import initManager from 'chat-box/utils/chat-init-manager'
import { setRequestConfig } from 'chat-box/utils/request'
import { setLoggingOut, resetReloginState } from '@/utils/request/relogin'

const useUserStore = defineStore(
  'user',
  {
    state: () => ({
      token: getToken(),
      name: '',
      avatar: '',
      admin: '',
      logo: '',
      visual: '',
      roles: [],
      permissions: [],
      isShowGemini: false,
      notice: null,
      userId: '',
    }),
    actions: {
      // 登录
      login(userInfo) {
        const username = userInfo.username.trim()
        const password = userInfo.password
        const code = userInfo.code
        const uuid = userInfo.uuid
        return new Promise((resolve, reject) => {
          login(username, password, code, uuid).then(res => {
            const token = res.data.access_token
            this.clearLocalSessionState()
            setToken(token)
            this.token = token
            resetReloginState()
            resolve()
          }).catch(error => {
            reject(error)
          })
        })
      },
      // 手机号登录
      phoneLogin(userInfo) {
        return new Promise((resolve, reject) => {
          phoneLogin(userInfo).then(res => {
            const token = res.data.access_token
            this.clearLocalSessionState()
            setToken(token)
            this.token = token
            resetReloginState()
            resolve()
          }).catch(error => {
            reject(error)
          })
        })
      },
      // 获取用户信息
      getInfo() {
        return new Promise((resolve, reject) => {
          const params = {
            systemSign: 'gemini3',
          }
          getInfo(params).then(res => {
            // 处理不同的响应结构：可能是 res.user 或 res.data.user
            let user = null;
            if (res && res.user) {
              user = res.user;
            } else if (res && res.data && res.data.user) {
              user = res.data.user;
            } else if (res && res.data && typeof res.data === 'object' && res.data.userName) {
              // 如果 data 本身就是用户对象
              user = res.data;
            }

            // 如果仍然没有找到用户信息，记录错误但继续执行（优雅降级）
            if (!user) {
              console.warn('getInfo: 无法找到用户信息，使用默认值', res);
              // 不拒绝 Promise，而是使用默认值继续执行
              user = {
                userId: this.userId || '',
                userName: this.name || '',
                avatar: '',
                logo: '',
                visualLogo: '',
                admin: ''
              };
            }

            // 保存原始响应中的其他信息
            const responseData = res?.data || res;

            const nextUserId = user.userId || user.id || '';

            if (nextUserId && ws_req.userId && ws_req.userId !== nextUserId) {
              ws_req.close();
              ws_req.userId = null;
            }

            // 只有在 userId 存在时才连接 WebSocket
            if (nextUserId && !ws_req.is_open && !ws_req.is_connecting) {
              ws_req.connect(nextUserId);
              console.warn('[ws-req] connect');
            } else {
              console.warn('[ws-req] userId not available, skip connect');
            }

            const avatar = (user.avatar == "" || user.avatar == null) ? defAva : user.avatar;
            const logo = (user.logo == "" || user.logo == null) ? defLogo : user.logo;
            const visual = (user.visualLogo == "" || user.visualLogo == null) ? defVisual : user.visualLogo;

            // 获取 roles 和 permissions（可能在不同位置）
            const roles = responseData?.roles || res?.roles;
            const permissions = responseData?.permissions || res?.permissions;

            if (roles && Array.isArray(roles) && roles.length > 0) {
              this.roles = roles;
              this.permissions = permissions || [];
            } else {
              this.roles = ['ROLE_DEFAULT'];
              this.permissions = [];
            }

            this.name = user.userName || user.name || '';
            this.userId = nextUserId;
            this.admin = user.admin || '';
            this.avatar = avatar;
            this.logo = logo;
            this.visual = visual;
            this.notice = responseData?.notice || res?.notice || null;

            // 检查 systems 是否存在（可能在不同位置）
            const systems = responseData?.systems || res?.systems;
            if (systems && Array.isArray(systems)) {
              systems.forEach(item => {
                if (item.systemSign === 'eMind-user') {
                  this.isShowGemini = true;
                  return;
                }
              });
            }

            resolve(res || responseData)
          }).catch(error => {
            reject(error)
          })
        })
      },
      // 退出系统（主动退出，调用后端接口）
      logOut() {
        // 置位后，退出过程中在途请求返回的 401 不再弹重新登录框
        setLoggingOut(true)
        return new Promise((resolve, reject) => {
          logout(this.token).then(() => {
            this.doLogoutCleanup()
            resolve()
          }).catch(error => {
            setLoggingOut(false)
            reject(error)
          })
        })
      },
      // 退出系统（被动退出，不调用后端接口）
      logOutWithoutAPI() {
        return new Promise((resolve) => {
          this.doLogoutCleanup()
          resolve()
        })
      },
      // 清理操作（共用）
      doLogoutCleanup() {
        // 删除 Cookie
        removeToken()

        // 先清空状态（Pinia 会自动同步到 localStorage）
        this.clearLocalSessionState()

        // 等待 Pinia 持久化完成后再删除 localStorage
        setTimeout(() => {
          localStorage.removeItem('user')
        }, 0)
      },
      clearLocalSessionState() {
        this.token = ''
        this.name = ''
        this.avatar = ''
        this.admin = ''
        this.logo = ''
        this.visual = ''
        this.roles = []
        this.permissions = []
        this.isShowGemini = false
        this.notice = null
        this.userId = ''

        // 关闭 WebSocket
        ws_req.close()
        ws_req.userId = null
        console.warn('[ws-req] close')

        // 重置 chat-init-manager
        initManager.reset()

        // 清除 request 配置
        setRequestConfig({
          token: '',
          appId: '',
          userId: '',
          chatId: '',
          sessionId: ''
        })

        // 重置电站状态,避免超时重登录后残留上一个账号的电站(app store 已开启持久化)
        try {
          const appStore = useAppStore()
          appStore.resetStationState()
        } catch (e) {
          console.error('[logout] reset station state failed:', e)
        }
      }
    },

    persist: true
  })

export default useUserStore
